import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import clsx from "clsx";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import Loader from "../../../../components/Loader/Loader";
import { sendPostRequest } from "../../../../actions/app-actions";
import {
  getLoggedInUserTimezone,
  notification,
} from "../../../../utils/utility";
import {
  buildProfileImageSrc,
  getDisplayInitials,
} from "../../../../utils/profileImage";
import { formatBookingDate } from "./formatBookingDate";
import "./scheduledEvents.scss";

const ReassignModal = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dataSet, setDataSet] = useState([]);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    const { data, sendPostRequest } = props;
    setIsLoading(true);
    sendPostRequest({
      url: `/get-staffs-to-reassign`,
      data: {
        ids: data,
        timezone: getLoggedInUserTimezone(),
      },
      onSuccess: function (response) {
        setIsLoading(false);
        setDataSet(response.data?.data || []);
      },
      onError: function () {
        setIsLoading(false);
        notification.error("Could not load team members. Please try again.");
      },
    });
  }, []);

  const bookingsNeedingStaff = useMemo(
    () => (dataSet || []).filter((item) => (item.staffs || []).length > 0),
    [dataSet]
  );

  const canSubmit =
    bookingsNeedingStaff.length > 0 &&
    bookingsNeedingStaff.every((item) =>
      bookings.some(
        (booking) =>
          String(booking.id) === String(item.id) && Boolean(booking.staffId)
      )
    );

  const onReassign = () => {
    if (!canSubmit) {
      return;
    }

    const { sendPostRequest, handleClose, loadUpcomingEvents } = props;
    setIsLoading(true);
    sendPostRequest({
      url: `/booking/bulk-reassign`,
      data: {
        bookings,
      },
      onSuccess: function () {
        setIsLoading(false);
        handleClose();
        loadUpcomingEvents();
        notification.success("Events successfully reassigned!");
      },
      onError: function () {
        setIsLoading(false);
        notification.error("Could not reassign these events. Please try again.");
      },
    });
  };

  const onStaffChangeHandler = (bookingId, staffId) => {
    setBookings((current) => {
      const next = current.filter(
        (booking) => String(booking.id) !== String(bookingId)
      );
      next.push({ id: bookingId, staffId });
      return next;
    });
  };

  const selectedStaffId = (bookingId) => {
    const match = bookings.find(
      (booking) => String(booking.id) === String(bookingId)
    );
    return match ? String(match.staffId) : "";
  };

  return (
    <>
      <Loader isShowLoader={isLoading} />
      <Modal
        isOpen={props.showReassignModal}
        toggle={props.handleClose}
        centered
        className="se-reassign-modal"
        modalClassName="se-reassign-modal-root"
        contentClassName="se-reassign-modal__content"
        size="md"
      >
        <ModalHeader toggle={props.handleClose}>
          Assign to a team member
        </ModalHeader>
        <ModalBody>
          <p className="se-reassign-modal__lead">
            Only people who are free at this time are shown.
          </p>

          {(dataSet || []).map((data) => {
            const staffs = data.staffs || [];
            const selected = selectedStaffId(data.id);
            return (
              <article className="se-reassign-item" key={data.id}>
                <header className="se-reassign-item__meta">
                  <div>
                    <p className="se-reassign-item__date">
                      {formatBookingDate(data.dated, "YYYY-MM-DD")}
                    </p>
                    <p className="se-reassign-item__hint">Booking time</p>
                  </div>
                  <span className="se-reassign-item__slot">{data.slot}</span>
                </header>

                {staffs.length > 0 ? (
                  <div
                    className="se-reassign-staff"
                    role="radiogroup"
                    aria-label="Available team members"
                  >
                    {staffs.map((staff) => {
                      const name = `${staff.first_name || ""} ${
                        staff.last_name || ""
                      }`.trim();
                      const photo = buildProfileImageSrc(staff.profile);
                      const inputId = `reassign-${data.id}-${staff.id}`;
                      const isChecked = selected === String(staff.id);
                      return (
                        <label
                          key={staff.id}
                          htmlFor={inputId}
                          className={clsx("se-reassign-staff__option", {
                            "is-selected": isChecked,
                          })}
                        >
                          <input
                            id={inputId}
                            type="radio"
                            name={`event-staff-${data.id}`}
                            value={staff.id}
                            checked={isChecked}
                            onChange={(event) => {
                              onStaffChangeHandler(data.id, event.target.value);
                            }}
                          />
                          <span className="se-reassign-staff__avatar" aria-hidden="true">
                            {photo ? (
                              <img src={photo} alt="" />
                            ) : (
                              getDisplayInitials(name) || "?"
                            )}
                          </span>
                          <span className="se-reassign-staff__name">{name}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className="se-reassign-empty" role="status">
                    <strong>No one is free at this time</strong>
                    <span>
                      Try another booking, or update this person’s availability.
                    </span>
                  </div>
                )}
              </article>
            );
          })}

          {!isLoading && dataSet.length === 0 ? (
            <div className="se-reassign-empty" role="status">
              <strong>Nothing to reassign</strong>
              <span>Select a booking from the list and try again.</span>
            </div>
          ) : null}

          <div className="se-reassign-modal__actions">
            <button
              type="button"
              className="se-btn se-btn--ghost"
              onClick={props.handleClose}
            >
              Cancel
            </button>
            {bookingsNeedingStaff.length > 0 ? (
              <button
                type="button"
                className="se-btn se-btn--primary"
                onClick={onReassign}
                disabled={!canSubmit || isLoading}
              >
                Reassign
              </button>
            ) : null}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default connect(null, {
  sendPostRequest,
})(ReassignModal);
